import React, { useEffect, useMemo, useState } from "react";

export type TripConsentFormState = {
  childName: string;
  dateBirth: string;
  className: string;
  parentNames: string;
  parentPhones: string;
  emergencyContact: string;
  emergencyPhone: string;
  doctorName: string;
  doctorPhone: string;
  allergies: string;
  medicalNotes: string;
  swimmingDiploma: boolean | null;
  tripConsent: boolean;
  /** Also keep doctor, emergency contact and health details in the child's health profile. */
  saveToHealthProfile?: boolean;
};

type TripConsentFormProps = {
  request: {
    id: string | number;
    expiresAt?: string | null;
    classroomName?: string | null;
    childId?: string | number;
    parent1Id?: string | number | null;
    parent2Id?: string | number | null;
    parent1ConsentedAt?: string | null;
    parent2ConsentedAt?: string | null;
    requiresDualConsent?: boolean;
    consentsRequired?: number | null;
    consentsGiven?: number | null;
    currentParentId?: string | number | null;
  } | null;
  form: TripConsentFormState | null;
  onChange: (field: keyof TripConsentFormState, value: string | boolean) => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitting?: boolean;
  error?: string | null;
  t: (key: string, options?: Record<string, unknown>) => string;
};

const readOnlyInputClass =
  "w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-600 shadow-inner outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400";

const editableInputClass =
  "w-full rounded-xl border border-emerald-200/70 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/50 dark:bg-slate-900/60 dark:text-gray-100 dark:focus:border-emerald-600 dark:focus:ring-emerald-800/50";

const getDateLocale = () => {
  const stored = typeof window !== "undefined" ? window.localStorage?.getItem("i18nextLng") : "";
  if (stored?.startsWith("nl")) return "nl-NL";
  if (stored?.startsWith("en")) return "en-US";
  return typeof navigator !== "undefined" && navigator.language ? navigator.language : "en-US";
};

const TripConsentForm: React.FC<TripConsentFormProps> = ({
  request,
  form,
  onChange,
  onSubmit,
  onCancel,
  submitting = false,
  error,
  t,
}) => {
  const [wasSubmitted, setWasSubmitted] = useState(false);
  const expiresLabel = useMemo(() => {
    if (!request?.expiresAt) return null;
    try {
      const raw = String(request.expiresAt).trim();
      if (!raw) return null;

      let date: Date | null = null;
      if (/^\d+$/.test(raw)) {
        const numeric = Number(raw);
        if (Number.isFinite(numeric)) {
          const ms = raw.length <= 10 ? numeric * 1000 : numeric;
          date = new Date(ms);
        }
      } else {
        let normalized = raw;
        if (normalized.includes(" ") && !normalized.includes("T")) {
          normalized = normalized.replace(" ", "T");
        }
        date = new Date(normalized);
      }

      if (!date || Number.isNaN(date.getTime())) return null;
      return date.toLocaleDateString(getDateLocale());
    } catch {
      return null;
    }
  }, [request?.expiresAt]);

  useEffect(() => {
    setWasSubmitted(false);
  }, [request?.id]);

  const parents = useMemo(() => {
    const splitItems = (value: string) =>
      value
        .split(/[,/&;]+/)
        .map((v) => v.trim())
        .filter(Boolean);
    const names = splitItems(form?.parentNames || "");
    const phones = splitItems(form?.parentPhones || "");
    return [0, 1].map((idx) => ({
      name: names[idx] ?? names[0] ?? "",
      phone: phones[idx] ?? "-",
    }));
  }, [form?.parentNames, form?.parentPhones]);

  if (!request || !form) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setWasSubmitted(true);
    onSubmit();
  };

  const update = (field: keyof TripConsentFormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (e.target.type === "checkbox") {
      onChange(field, (e.target as HTMLInputElement).checked);
    } else if (field === "swimmingDiploma") {
      const value = e.target.value;
      if (value === "yes") onChange(field, true);
      else if (value === "no") onChange(field, false);
      else onChange(field, null);
    } else {
      onChange(field, e.target.value);
    }
  };

  const consentGiven = !!form.tripConsent;
  const dualConsent = !!request?.requiresDualConsent;
  const consentsGiven = request?.consentsGiven ?? 0;
  const parent1Done = !!request?.parent1ConsentedAt;
  const parent2Done = !!request?.parent2ConsentedAt;
  const currentParentId = request?.currentParentId;
  const isParent1 = currentParentId && request?.parent1Id && String(request.parent1Id) === String(currentParentId);
  const isParent2 = currentParentId && request?.parent2Id && String(request.parent2Id) === String(currentParentId);
  const firstConsentDone = dualConsent && consentsGiven >= 1;
  const fieldsDisabled = submitting || firstConsentDone;
  const currentParentAlreadyConsented = (isParent1 && parent1Done) || (isParent2 && parent2Done);
  const consentDisabled = submitting || currentParentAlreadyConsented;

  const showApproveOnly =
    dualConsent &&
    !currentParentAlreadyConsented &&
    firstConsentDone &&
    ((isParent1 && parent2Done) || (isParent2 && parent1Done));

  const shouldHideForm = currentParentAlreadyConsented;

  const handleQuickSubmit = (approved: boolean) => {
    if (submitting || consentDisabled) return;
    onChange("tripConsent", approved);
    onSubmit();
  };

  if (shouldHideForm) {
    return (
      <div className="rounded-3xl border border-emerald-100/70 bg-white/95 p-5 shadow-xl shadow-emerald-50/70 dark:border-emerald-900/40 dark:bg-slate-900/70 dark:shadow-emerald-900/20">
        <p className="text-sm text-slate-700 dark:text-slate-200">
          {t("parentPortal.trip_consent.dual_consent_waiting", {
            defaultValue: "Your consent has been received. Waiting for the other parent's approval.",
          })}
        </p>
        <div className="mt-3 rounded-xl border border-amber-100 bg-amber-50/80 px-3 py-2 text-xs text-amber-800 dark:border-amber-800/50 dark:bg-amber-900/30 dark:text-amber-100">
          {t("parentPortal.trip_consent.dual_consent_status", {
            defaultValue: "Status: {{given}} of {{required}} approved",
            given: request?.consentsGiven ?? 1,
            required: request?.consentsRequired ?? 2,
          })}
        </div>
      </div>
    );
  }

  if (showApproveOnly) {
    return (
      <div className="rounded-3xl border border-emerald-100/70 bg-white/95 p-5 shadow-xl shadow-emerald-50/70 dark:border-emerald-900/40 dark:bg-slate-900/70 dark:shadow-emerald-900/20">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-slate-700 dark:text-slate-200">
            {t("parentPortal.trip_consent.dual_consent_pending", {
              defaultValue: "The details have been filled in. Approve or reject.",
            })}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => handleQuickSubmit(true)}
              disabled={submitting}
              className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-500 disabled:opacity-60"
            >
              {t("parentPortal.trip_consent.approve", { defaultValue: "Approve" })}
            </button>
            <button
              type="button"
              onClick={() => handleQuickSubmit(false)}
              disabled={submitting}
              className="rounded-full bg-rose-100 px-4 py-2 text-sm font-semibold text-rose-700 shadow hover:bg-rose-200 disabled:opacity-60"
            >
              {t("parentPortal.trip_consent.reject", { defaultValue: "Reject" })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-emerald-100/70 bg-white/95 p-5 shadow-xl shadow-emerald-50/70 dark:border-emerald-900/40 dark:bg-slate-900/70 dark:shadow-emerald-900/20">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-200">
            {t("parentPortal.trip_consent.title", { defaultValue: "Trip consent" })}
          </p>
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white">
            {form.childName || t("common.unknown_child", { defaultValue: "Child" })}
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {request.classroomName ||
              t("parentPortal.trip_consent.classroom_fallback", { defaultValue: "Current class" })}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 text-sm">
          {expiresLabel && (
            <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-100">
              {t("parentPortal.trip_consent.expires", {
                defaultValue: "Expires on {{date}}",
                date: expiresLabel,
              })}
            </span>
          )}
          <button
            type="button"
            onClick={onCancel}
            className="lumen-glow-sheen inline-flex items-center rounded-full border border-gray-200/70 bg-white/80 px-3 py-1 text-xs font-semibold text-gray-700 transition hover:text-gray-900 dark:border-gray-700/60 dark:bg-slate-800/70 dark:text-gray-200"
          >
            {t("common.actions.close", { defaultValue: "Close" })}
          </button>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-emerald-100/70 bg-emerald-50/70 px-4 py-3 text-sm text-emerald-900 shadow-sm dark:border-emerald-800/40 dark:bg-emerald-900/40 dark:text-emerald-100">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={form.tripConsent}
            onChange={update("tripConsent")}
            className="mt-1 h-5 w-5 rounded border-emerald-500 text-emerald-600 focus:ring-emerald-500"
            disabled={consentDisabled}
          />
          <span className="leading-relaxed">
            {t("parentPortal.trip_consent.consent_label", {
              defaultValue: "I give permission for participation in the trip and for the use of the above information in emergencies.",
            })}
          </span>
        </label>
        {firstConsentDone && (
          <p className="mt-2 text-xs text-emerald-800 dark:text-emerald-100">
            {t("parentPortal.trip_consent.dual_consent_note", {
              defaultValue: "The details have been filled in. You only need to approve.",
            })}
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="mt-4 space-y-5">
        <fieldset
          disabled={fieldsDisabled}
          aria-disabled={fieldsDisabled}
          className={`space-y-5 ${fieldsDisabled ? "opacity-60" : ""}`}
        >
          <p className="rounded-2xl border border-emerald-100/60 bg-emerald-50/80 px-3 py-2 text-xs text-emerald-800 shadow-inner dark:border-emerald-800/40 dark:bg-emerald-900/30 dark:text-emerald-100">
            {t("parentPortal.trip_consent.helper", {
              defaultValue: "Fields already known from the system are gray and read-only. Check the details and fill in the missing information.",
            })}
          </p>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200/70 bg-slate-50/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/50">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                {t("parentPortal.trip_consent.section_child", { defaultValue: "Child details" })}
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="space-y-1 text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    {t("parentPortal.trip_consent.child_name", { defaultValue: "Child name" })}
                  </span>
                  <input value={form.childName} readOnly className={readOnlyInputClass} />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    {t("parentPortal.trip_consent.date_birth", { defaultValue: "Date of birth" })}
                  </span>
                  <input value={form.dateBirth} readOnly className={readOnlyInputClass} />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    {t("parentPortal.trip_consent.class", { defaultValue: "Class" })}
                  </span>
                  <input value={form.className} readOnly className={readOnlyInputClass} />
                </label>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200/70 bg-slate-50/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/50">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                {t("parentPortal.trip_consent.section_parents", { defaultValue: "Parents / guardians" })}
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {parents.map((parent, idx) => (
                  <div key={`parent-${idx}`} className="space-y-2">
                    <label className="space-y-1 text-sm">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        {t("parentPortal.trip_consent.parent_label", {
                          defaultValue: "Parent/guardian {{idx}}",
                          idx: idx + 1,
                        })}
                      </span>
                      <input value={parent.name} readOnly className={readOnlyInputClass} />
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        {t("parentPortal.trip_consent.parent_phone_label", {
                          defaultValue: "Phone {{idx}}",
                          idx: idx + 1,
                        })}
                      </span>
                      <input value={parent.phone} readOnly className={readOnlyInputClass} />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-emerald-100/70 bg-white p-4 shadow-sm dark:border-emerald-800/40 dark:bg-slate-900/60">
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-200">
                {t("parentPortal.trip_consent.section_contacts", { defaultValue: "Emergency contact & doctor" })}
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="space-y-1 text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    {t("parentPortal.trip_consent.emergency_contact", { defaultValue: "Emergency contact (name)" })}
                  </span>
                  <input
                    value={form.emergencyContact}
                    onChange={update("emergencyContact")}
                    className={editableInputClass}
                    required
                    disabled={fieldsDisabled}
                  />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    {t("parentPortal.trip_consent.emergency_phone", { defaultValue: "Emergency contact phone" })}
                  </span>
                  <input
                    value={form.emergencyPhone}
                    onChange={update("emergencyPhone")}
                    className={editableInputClass}
                    required
                    disabled={fieldsDisabled}
                  />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    {t("parentPortal.trip_consent.gp_name", { defaultValue: "Doctor name" })}
                  </span>
                  <input
                    value={form.doctorName}
                    onChange={update("doctorName")}
                    className={editableInputClass}
                    required
                    disabled={fieldsDisabled}
                  />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    {t("parentPortal.trip_consent.gp_phone", { defaultValue: "Doctor phone" })}
                  </span>
                  <input
                    value={form.doctorPhone}
                    onChange={update("doctorPhone")}
                    className={editableInputClass}
                    required
                    disabled={fieldsDisabled}
                  />
                </label>
              </div>
            </div>

            <div className="rounded-2xl border border-amber-100/70 bg-white p-4 shadow-sm dark:border-amber-800/40 dark:bg-slate-900/60">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-200">
                {t("parentPortal.trip_consent.section_health", { defaultValue: "Medical information" })}
              </p>
              <div className="mt-3 grid gap-3">
                <label className="space-y-1 text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    {t("parentPortal.trip_consent.allergies", { defaultValue: "Allergies" })}
                  </span>
                  <textarea
                    value={form.allergies}
                    onChange={update("allergies")}
                    rows={3}
                    className={`${editableInputClass} min-h-[96px] resize-y`}
                    placeholder={t("parentPortal.trip_consent.allergies_hint", { defaultValue: "For example peanuts, pets, medication" })}
                    disabled={fieldsDisabled}
                  />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    {t("parentPortal.trip_consent.medical", { defaultValue: "Medical notes" })}
                  </span>
                  <textarea
                    value={form.medicalNotes}
                    onChange={update("medicalNotes")}
                    rows={3}
                    className={`${editableInputClass} min-h-[96px] resize-y`}
                    placeholder={t("parentPortal.trip_consent.medical_hint", { defaultValue: "For example medication, permissions, special notes" })}
                    disabled={fieldsDisabled}
                  />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    {t("parentPortal.trip_consent.swimming", { defaultValue: "Swimming diploma" })}
                  </span>
                  <select
                    value={form.swimmingDiploma === true ? "yes" : form.swimmingDiploma === false ? "no" : "unknown"}
                    onChange={update("swimmingDiploma")}
                    className={editableInputClass}
                    disabled={fieldsDisabled}
                  >
                    <option value="unknown">{t("parentPortal.trip_consent.swimming_unknown", { defaultValue: "Unknown" })}</option>
                    <option value="yes">{t("common.yes", { defaultValue: "Yes" })}</option>
                    <option value="no">{t("common.no", { defaultValue: "No" })}</option>
                  </select>
                </label>
              </div>
            </div>
          </div>

          <label className="flex items-start gap-2 rounded-xl border border-emerald-100/80 bg-emerald-50/60 px-3 py-2 text-sm text-slate-700 dark:border-emerald-900/40 dark:bg-emerald-950/20 dark:text-slate-200">
            <input
              type="checkbox"
              className="mt-1"
              checked={!!form.saveToHealthProfile}
              onChange={update("saveToHealthProfile")}
              disabled={fieldsDisabled}
            />
            <span>
              {t("parentPortal.health.save_from_trip", {
                defaultValue:
                  "Also keep these details for next time (only visible to you and the other parent, under your child in the Family tab)",
              })}
            </span>
          </label>

          {error && (
            <p className="rounded-xl border border-amber-200/80 bg-amber-50/80 px-3 py-2 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-900/30 dark:text-amber-100">
              {error}
            </p>
          )}
        </fieldset>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="lumen-glow-sheen inline-flex items-center rounded-full border border-gray-200/70 bg-white/90 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:text-gray-900 dark:border-gray-700/60 dark:bg-slate-800/80 dark:text-gray-100"
          >
            {t("common.actions.cancel")}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="lumen-glow-sheen inline-flex items-center rounded-full border border-emerald-200/70 bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-500 disabled:cursor-wait disabled:opacity-80 dark:border-emerald-800/60 dark:bg-emerald-700 dark:hover:bg-emerald-600"
          >
            {submitting
              ? t("common.actions.saving", { defaultValue: "Saving..." })
              : t("parentPortal.trip_consent.submit", { defaultValue: "Submit form" })}
          </button>
        </div>
      </form>

      {wasSubmitted && !error && submitting && (
        <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-200">
          {t("parentPortal.trip_consent.saving_hint", { defaultValue: "The details are being saved and shared with the teacher." })}
        </p>
      )}
    </div>
  );
};

export default TripConsentForm;
